/**
 * Integrytas Group - Email & Communication Templates (ECM)
 * Page: ECM.TEMPLATES
 *
 * The master communication-template registry: channel, primary entity, related
 * bindings, localized subject/body (tokens + {{#section}}), and a test-send.
 * MTA Business-Events actions reference these templates by code.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { hasAccess, getForensicActorId  } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { sendForRecord } from '../../services/ecm/EcmService';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';

const PAGE_CODE = 'ECM.TEMPLATES';
registerUdcHost(PAGE_CODE, 'ecm_template', 'Communication Templates');

const LOCALES = ['EN_US', 'ES_MX'];

const TEMPLATE_FIELD_DEFS: FieldDef[] = [
  { key: 'code', type: 'text', label: 'Code', required: true, col_span: 1 },
  { key: 'name', type: 'text', label: 'Name', col_span: 1 },
  { key: 'channel', type: 'select', label: 'Channel', col_span: 1 },
  { key: 'entity', type: 'text', label: 'Primary Entity', col_span: 1 },
  { key: 'pk', type: 'text', label: 'PK Column', col_span: 1 },
];
const TEMPLATE_CORE_LAYOUT = toUldCoreLayout(TEMPLATE_FIELD_DEFS);

type Channel = 'EMAIL' | 'INAPP' | 'SMS' | 'WEBHOOK';
interface TemplateRow { ecm_template_id: number; internal_code: string; name: string; channel: Channel; primary_entity: string | null; pk_column: string | null; category: string | null; is_active: 'Y' | 'N' }
interface TlRow { locale_code: string; subject: string | null; body_html: string | null }
interface BindingRow { ecm_binding_id: number; binding_name: string; related_entity: string; fk_column: string; order_by: string | null }

export interface EcmTemplateManagerPageProps { session: AuthSession }

export function EcmTemplateManagerPage({ session }: EcmTemplateManagerPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <EcmTemplateManagerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function EcmTemplateManagerPageInner({ session }: EcmTemplateManagerPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const TEMPLATE_GRID_COLS: GridColumnDef[] = [
    { key: 'internal_code',  header: t('ecm.col.code', 'Code'),       defaultWidth: 220 },
    { key: 'name',           header: t('ecm.col.name', 'Name'),       defaultWidth: 220 },
    { key: 'channel',        header: t('ecm.col.channel', 'Channel'), defaultWidth: 120 },
    { key: 'primary_entity', header: t('ecm.col.entity', 'Entity'),   defaultWidth: 180 },
    { key: 'is_active',      header: t('ecm.col.active', 'Active'),    defaultWidth: 90  },
  ];
  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<TemplateRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TemplateRow | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ channel: string[]; entity: string[]; active: string[] }>({ channel: [], entity: [], active: ['Y'] });
  const udcGrid = useUdcGrid('ecm_template', PAGE_CODE, { pkColumn: 'ecm_template_id', rows: rows as unknown as Record<string, unknown>[] });
  // §17.B13 related-children disclosure. This list loads no per-row child
  // collections (bindings / localized _tl live in the editor modal), so the
  // control renders inert until relations are wired.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => []; // TODO: wire relations
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('ecm_template')
        .select('ecm_template_id, internal_code, name, channel, primary_entity, pk_column, category, is_active').order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as TemplateRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: (string | null | undefined)[]) => [...new Set(vals.filter((v): v is string => !!v))].sort();
    return {
      channel: uniq(rows.map(r => r.channel)).map(v => ({ value: v, label: v })),
      entity:  uniq(rows.map(r => r.primary_entity)).map(v => ({ value: v, label: v })),
      active:  [{ value: 'Y', label: t('ecm.facet.active', 'Active') }, { value: 'N', label: t('ecm.facet.inactive', 'Inactive') }],
    };
  }, [rows, t]);

  const facetsActive = facets.channel.length + facets.entity.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ channel: [], entity: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const matchesFacets = (r: TemplateRow) =>
      (facets.channel.length === 0 || facets.channel.includes(r.channel)) &&
      (facets.entity.length  === 0 || (r.primary_entity != null && facets.entity.includes(r.primary_entity))) &&
      (facets.active.length  === 0 || facets.active.includes(r.is_active));
    return rows.filter(r =>
      (!q ||
        r.internal_code.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        (r.primary_entity ?? '').toLowerCase().includes(q))
      && matchesFacets(r)
      && udcGrid.matchRow(r as unknown as Record<string, unknown>)
    );
  }, [rows, filter, facets, udcGrid]);

  const handleDelete = async (row: TemplateRow) => {
    const { error: e } = await routedDelete(session, 'ecm_template', 'ecm_template_id', row.ecm_template_id);
    if (e) { setError(e.message); return; }
    setDeleteTarget(null);
    void reload();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('ecm.title', 'Communication Templates')}
          description={t('ecm.list.description', 'Localized email, in-app, SMS and webhook templates that business events send.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('ecm.refresh', 'Refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('ecm.new_template', 'New Template')}
              </button>)}
            </>
          }
        />
        <StatStrip
          stats={[
            { value: rows.length, label: t('ecm.stat.total', 'Templates') },
            { value: rows.filter(r => r.is_active === 'Y').length, label: t('ecm.stat.active', 'Active') },
          ]}
        />
        {error && (
          <ErrorBanner
            message={t('ecm.error_banner', "Couldn't complete the last communication-template operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('ecm.filter_ph', 'Filter templates…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('ecm.col.channel', 'Channel')} options={facetOptions.channel} selected={facets.channel} onChange={v => setFacets(f => ({ ...f, channel: v }))} />
            <FacetFilter label={t('ecm.col.entity', 'Entity')}   options={facetOptions.entity}  selected={facets.entity}  onChange={v => setFacets(f => ({ ...f, entity: v }))} />
            <FacetFilter label={t('ecm.col.active', 'Active')}   options={facetOptions.active}  selected={facets.active}  onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-watermark">{t('ecm.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={[...TEMPLATE_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('ecm.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('ecm.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.ecm_template_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.ecm_template_id)} hasChildren={hasRelated(r.ecm_template_id)} onToggle={() => detail.toggle(r.ecm_template_id)} expandLabel={t('ecm.related.expand', 'Show related records')} collapseLabel={t('ecm.related.collapse', 'Hide related')} tooltip={t('ecm.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code'  && <span className="font-mono text-csm-sm">{r.internal_code}</span>}
                        {col.key === 'name'           && <span className="text-csm-sm">{r.name}</span>}
                        {col.key === 'channel'        && <span className="text-csm-xs">{r.channel}</span>}
                        {col.key === 'primary_entity' && <span className="font-mono text-csm-xs">{r.primary_entity ?? '—'}</span>}
                        {col.key === 'is_active'      && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {udcGrid.isUdcKey(col.key)    && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('ecm.edit', 'Edit')}</button>)}
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('ecm.delete', 'Delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.ecm_template_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.ecm_template_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('ecm.no_templates', 'No templates yet')} action={canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('ecm.new_template_plus', '+ New Template')}</button> : undefined} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
      {(editing || creating) && (
        <TemplateModal session={session} row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
      {deleteTarget && (
        <ConfirmModal
          title={t('ecm.delete_template_title', 'Delete Template?')}
          message={t('ecm.delete_template_msg', 'Delete "{{name}}"? This cannot be undone.', { name: deleteTarget.name })}
          onConfirm={() => void handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function TemplateModal({ session, row, onClose, onSaved }: { session: AuthSession; row: TemplateRow | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const actor = getForensicActorId(session);
  const id = row?.ecm_template_id;

  const initialValues = {
    code: row?.internal_code ?? '',
    name: row?.name ?? '',
    channel: (row?.channel ?? 'EMAIL') as Channel,
    entity: row?.primary_entity ?? '',
    pk: row?.pk_column ?? '',
    active: row ? row.is_active === 'Y' : false,
  };

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  const udcForm = useUdcFormFields('ecm_template', PAGE_CODE, { pkColumn: 'ecm_template_id', pk: row?.ecm_template_id });
  const { layout } = useUldLayout('ECM.TEMPLATES.MODAL', useMemo(() => [...TEMPLATE_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]));
  const [tl, setTl] = useState<Record<string, TlRow>>({ EN_US: { locale_code: 'EN_US', subject: '', body_html: '' }, ES_MX: { locale_code: 'ES_MX', subject: '', body_html: '' } });
  const [bindings, setBindings] = useState<BindingRow[]>([]);
  const [testId, setTestId] = useState('');
  const [testRecips, setTestRecips] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);

  const loadChildren = async () => {
    if (!id) return;
    const [t, b] = await Promise.all([
      apiDb.from('ecm_template_tl').select('locale_code, subject, body_html').eq('ecm_template_id', id),
      apiDb.from('ecm_binding').select('ecm_binding_id, binding_name, related_entity, fk_column, order_by').eq('ecm_template_id', id),
    ]);
    const next = { EN_US: { locale_code: 'EN_US', subject: '', body_html: '' }, ES_MX: { locale_code: 'ES_MX', subject: '', body_html: '' } } as Record<string, TlRow>;
    ((t.data ?? []) as TlRow[]).forEach((r) => { next[r.locale_code] = { locale_code: r.locale_code, subject: r.subject ?? '', body_html: r.body_html ?? '' }; });
    setTl(next); setBindings((b.data ?? []) as BindingRow[]);
  };

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
    void loadChildren();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const validateCode = (val: string) => {
    if (!val) { setInternalCodeError(null); return; }
    if (!/^[A-Z][A-Z0-9_.]*$/.test(val)) {
      setInternalCodeError(t('ecm.code_format_error', 'Must start with a letter — uppercase letters, numbers, dots only.'));
    } else {
      setInternalCodeError(null);
    }
  };

  const saveTemplate = async () => {
    setBusy(true); setError(null);
    try {
      if (isCreate && !form.code.trim()) throw new Error(t('ecm.code_required', 'Code is required.'));
      if (internalCodeError) throw new Error(internalCodeError);
      const payload = { name: form.name.trim(), channel: form.channel, primary_entity: form.entity.trim() || null, pk_column: form.pk.trim() || null, is_active: form.active ? 'Y' : 'N' };
      let tid = id;
      if (isCreate) {
        const { row_id, error: e } = await routedInsert(session, 'ecm_template', { internal_code: form.code.trim().toUpperCase(), ...payload });
        if (e) throw e; tid = row_id ?? undefined;
      } else { const { error: e } = await routedUpdate(session, 'ecm_template', 'ecm_template_id', id, payload); if (e) throw e; }
      const tlRows = LOCALES.map(l => tl[l]).filter(t => (t.subject ?? '').trim() || (t.body_html ?? '').trim())
        .map(t => ({ ecm_template_id: tid, locale_code: t.locale_code, subject: t.subject || null, body_html: t.body_html || null, modified_by: actor }));
      if (tlRows.length) { const { error: e } = await apiDb.from('ecm_template_tl').upsert(tlRows, { onConflict: 'ecm_template_id,locale_code' }); if (e) throw e; }
      reset(form);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addBinding = async () => { if (!id) return; await routedInsert(session, 'ecm_binding', { ecm_template_id: id, binding_name: '', related_entity: '', fk_column: '' }); await loadChildren(); };
  const saveBinding = async (b: BindingRow) => { await routedUpdate(session, 'ecm_binding', 'ecm_binding_id', b.ecm_binding_id, { binding_name: b.binding_name, related_entity: b.related_entity, fk_column: b.fk_column, order_by: b.order_by || null }); };
  const delBinding = async (bid: number) => { await routedDelete(session, 'ecm_binding', 'ecm_binding_id', bid); setBindings(bs => bs.filter(x => x.ecm_binding_id !== bid)); };

  const testSend = async () => {
    if (!row || !testId.trim()) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const recipients = testRecips.split(',').map(s => s.trim()).filter(Boolean);
      const res = await sendForRecord(row.internal_code, testId.trim(), { session, recipients });
      setNotice(`${res.channel}: ${res.status}${res.messageId ? ` ${t('ecm.msg_id', '(msg #{{id}})', { id: res.messageId })}` : ''}.`);
      if (res.channel === 'EMAIL') { const w = window.open('', '_blank'); if (w) { w.document.write(`<h3>${res.subject}</h3>` + res.body_html); w.document.close(); } }
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <>
      <Modal
        title={isCreate ? t('ecm.create_template', 'Create Template') : t('ecm.edit_template', 'Edit Template — {{code}}', { code: row?.internal_code ?? '' })}
        onClose={handleClose}
        width="3xl"
        headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
        footer={
          <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('ecm.close', 'Close')}</button>
        }
      >
        <div className="mb-3">
          <FieldRenderer
            fields={useMemo(() => [...TEMPLATE_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs])}
            layout={layout}
            columns={3}
            pageCode="ECM.TEMPLATES.MODAL"
            session={session}
            render={(f) => {
              if (f.key === 'code') return (
                <PersonalizedField componentKey="modal" fieldKey="code" label={t('ecm.field.code', 'Code')}>
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('ecm.field.code', 'Code')}</label>
                  <input type="text" value={form.code} disabled={!isCreate}
                         onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                         onBlur={e => validateCode(e.target.value)}
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                  {internalCodeError
                    ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                    : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('ecm.code_hint', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>}
                </>
                </PersonalizedField>
              );
              if (f.key === 'name') return (
                <PersonalizedField componentKey="modal" fieldKey="name" label={t('ecm.field.name', 'Name')}>
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('ecm.field.name', 'Name')}</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></>
                </PersonalizedField>
              );
              if (f.key === 'channel') return (
                <PersonalizedField componentKey="modal" fieldKey="channel" label={t('ecm.field.channel', 'Channel')}>
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('ecm.field.channel', 'Channel')}</label>
                  <select value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value as Channel }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">{['EMAIL', 'INAPP', 'SMS', 'WEBHOOK'].map(c => <option key={c} value={c}>{c}</option>)}</select></>
                </PersonalizedField>
              );
              if (f.key === 'entity') return (
                <PersonalizedField componentKey="modal" fieldKey="entity" label={t('ecm.field.entity', 'Primary Entity')}>
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('ecm.field.entity', 'Primary Entity')}</label>
                  <input type="text" value={form.entity} onChange={e => setForm(f => ({ ...f, entity: e.target.value }))} placeholder={t('ecm.entity_ph', 'po_purchase_order')} className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></>
                </PersonalizedField>
              );
              if (f.key === 'pk') return (
                <PersonalizedField componentKey="modal" fieldKey="pk" label={t('ecm.field.pk', 'PK Column')}>
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('ecm.field.pk', 'PK Column')}</label>
                  <input type="text" value={form.pk} onChange={e => setForm(f => ({ ...f, pk: e.target.value }))} placeholder={t('ecm.pk_ph', 'po_id')} className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></>
                </PersonalizedField>
              );
              if (udcForm.isUdcKey(f.key)) return (
                <PersonalizedField componentKey="modal" fieldKey={f.key} label={f.label}
                  readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                  {udcForm.renderControl(f.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>

        {LOCALES.map(loc => (
          <div key={loc} className="mb-3 rounded border border-csm-border-soft p-2">
            <div className="mb-1 text-csm-xs font-semibold text-csm-ink-soft">{loc}</div>
            <input type="text" value={tl[loc]?.subject ?? ''} onChange={e => setTl(t => ({ ...t, [loc]: { ...t[loc], locale_code: loc, subject: e.target.value } }))} placeholder={t('ecm.subject_ph', 'Subject (tokens allowed)')} className="mb-1 h-8 w-full rounded border border-csm-border px-2 text-csm-sm" />
            <textarea rows={5} value={tl[loc]?.body_html ?? ''} onChange={e => setTl(t => ({ ...t, [loc]: { ...t[loc], locale_code: loc, body_html: e.target.value } }))} placeholder={t('ecm.body_html_ph', 'Body HTML — {{field}} and {{#section name}}…{{row.field}}…{{/section}}')} className="w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-xs" />
          </div>
        ))}

        <div className="mb-3 flex items-center gap-3">
          <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft"><input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-gray-700" /> {t('ecm.active', 'Active')}</label>
          <button onClick={() => void saveTemplate()} disabled={busy} className="ml-auto h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('ecm.saving', 'Saving…') : t('ecm.save_template', 'Save Template')}</button>
        </div>

        {!isCreate && (
          <>
            <div className="mb-3 border-t border-csm-border pt-2">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('ecm.related_bindings', 'Related Bindings')}</span>
                <button onClick={() => void addBinding()} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('ecm.add_binding', '+ Binding')}</button>
              </div>
              {bindings.map((b, i) => (
                <div key={b.ecm_binding_id} className="mb-1 flex flex-wrap items-center gap-1">
                  <input value={b.binding_name} onChange={e => setBindings(bs => bs.map((x, j) => j === i ? { ...x, binding_name: e.target.value } : x))} onBlur={() => void saveBinding(bindings[i])} placeholder={t('ecm.section_name_ph', 'section name')} className="h-7 w-32 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <input value={b.related_entity} onChange={e => setBindings(bs => bs.map((x, j) => j === i ? { ...x, related_entity: e.target.value } : x))} onBlur={() => void saveBinding(bindings[i])} placeholder={t('ecm.child_table_ph', 'child table')} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <input value={b.fk_column} onChange={e => setBindings(bs => bs.map((x, j) => j === i ? { ...x, fk_column: e.target.value } : x))} onBlur={() => void saveBinding(bindings[i])} placeholder={t('ecm.fk_column_ph', 'fk column')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <button onClick={() => void delBinding(b.ecm_binding_id)} className="text-csm-danger text-csm-xs">{t('ecm.delete_lc', 'delete')}</button>
                </div>
              ))}
            </div>
            <div className="border-t border-csm-border pt-2">
              <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('ecm.test_send', 'Test Send')}</div>
              <div className="flex flex-wrap items-center gap-2">
                <input value={testId} onChange={e => setTestId(e.target.value)} placeholder={t('ecm.record_id_ph', 'record id')} className="h-8 w-32 rounded border border-csm-border px-2 font-mono text-csm-sm" />
                <input value={testRecips} onChange={e => setTestRecips(e.target.value)} placeholder={t('ecm.recipients_ph', 'recipients (ids/emails, comma)')} className="h-8 w-64 rounded border border-csm-border px-2 font-mono text-csm-sm" />
                <button onClick={() => void testSend()} disabled={busy} className="h-8 rounded border border-csm-border bg-white px-3 text-csm-sm hover:bg-csm-surface-alt disabled:opacity-60">{t('ecm.send', 'Send')}</button>
                {notice && <span className="text-csm-xs text-primary">{notice}</span>}
              </div>
            </div>
          </>
        )}

        {error &&<div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
        {isCreate && <div className="mt-2 text-csm-2xs text-csm-watermark">{t('ecm.save_first', 'Save the template first to add bindings and test send.')}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('ecm.discard_title', 'Discard changes?')}
          message={t('ecm.discard_msg', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('ecm.discard', 'Discard')}
          cancelLabel={t('ecm.keep_editing', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(initialValues); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}
